import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useDomainStore, useFamilyStore } from '@/hooks/useStores';
import type { ProfileLocationState } from '@/types/profile-navigation';
import { getProfileBreadcrumbParent, resolveProfileNavigationContext } from '@/utils/profileNavigation';
import './breadcrumb.css';

const MY_FAMILIES_PATH = '/families';

const Breadcrumb: React.FC = observer(() => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const location = useLocation();
  const { familyId, familyMemberId } = useParams<{ familyId?: string; familyMemberId?: string }>();
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();

  const pathMap = useMemo(
    () => ({
      families: t('breadcrumb.pathMap.families'),
      'create-family': t('breadcrumb.pathMap.create-family'),
      profile: t('breadcrumb.pathMap.profile'),
      overview: t('breadcrumb.pathMap.overview'),
      'hybrid-pedigree': t('breadcrumb.pathMap.hybrid-pedigree'),
      members: t('breadcrumb.pathMap.members'),
      memories: t('breadcrumb.pathMap.memories'),
      add: t('breadcrumb.pathMap.add'),
      pets: t('breadcrumb.pathMap.pets'),
      media: t('breadcrumb.pathMap.media'),
      sources: t('breadcrumb.pathMap.sources'),
      events: t('breadcrumb.pathMap.events'),
      activity: t('breadcrumb.pathMap.activity'),
      review: t('breadcrumb.pathMap.review'),
      suggestions: t('breadcrumb.pathMap.suggestions'),
      invite: t('breadcrumb.pathMap.invite'),
      upload: t('breadcrumb.pathMap.upload'),
      changelog: t('breadcrumb.pathMap.changelog'),
      help: t('breadcrumb.pathMap.help')
    }),
    [t]
  );

  const helpTabLabels = useMemo(
    () => ({
      faqs: t('breadcrumb.helpTabs.faqs'),
      docs: t('breadcrumb.helpTabs.docs'),
      chat: t('breadcrumb.helpTabs.chat'),
      contact: t('breadcrumb.helpTabs.contact')
    }),
    [t]
  );

  // Extract route segments, ignoring UUIDs and other dynamic values
  const currentPath = location.pathname;
  const segments = currentPath.split('/').filter(Boolean);

  // Check if we're on a person or pet profile page
  const isPersonProfilePage = segments.includes('people') && familyMemberId && !segments.includes('add');
  const isPetProfilePage = segments.includes('pets') && familyMemberId && !segments.includes('add');

  // Get person name if on person profile page
  let personName: string | null = null;
  if (isPersonProfilePage && familyMemberId) {
    const person = familyStore.getPerson(familyMemberId);
    if (person) {
      personName = person.fullName || `${person.firstName} ${person.lastName}`.trim() || tCommon('unknown');
    }
  }

  let petName: string | null = null;
  if (isPetProfilePage && familyMemberId) {
    const pet = familyStore.getPet(familyMemberId);
    petName = pet?.displayName?.trim() || null;
  }

  // Check if we're in edit mode (via URL search params)
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get('edit') === 'true';

  // Check if we're on the help page and get the active tab
  const isHelpPage = segments.includes('help');
  const helpTab = searchParams.get('tab');
  const helpTabLabel = helpTab ? helpTabLabels[helpTab as keyof typeof helpTabLabels] : null;

  // Find the last meaningful segment (not a UUID or 'user')
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const meaningfulSegment = segments.reverse().find((seg) => seg !== 'user' && !uuidPattern.test(seg));

  // Check if we're in a family context
  const isInFamilyContext = familyId && domainStore.currentFamily;

  // Check if we're on a Main-level route (families, profile, create-family, help, changelog)
  const isMainLevelRoute =
    currentPath === '/families' ||
    currentPath === '/' ||
    currentPath.startsWith('/profile') ||
    currentPath.startsWith('/create-family') ||
    currentPath.startsWith('/help') ||
    currentPath.startsWith('/changelog');

  const breadcrumbItems: Array<{ label: string; path: string; isClickable: boolean }> = [];

  const locationState = location.state as ProfileLocationState | undefined;
  const isMemberProfilePage = isPersonProfilePage || isPetProfilePage;
  const profileNav =
    familyId && familyMemberId && isMemberProfilePage
      ? resolveProfileNavigationContext(familyId, isPetProfilePage ? 'pets' : 'people', familyMemberId, locationState)
      : undefined;
  const profileParent = familyId && profileNav ? getProfileBreadcrumbParent(familyId, profileNav) : undefined;

  const profileParentLabel = (): string | undefined => {
    if (!profileNav?.from) return profileParent?.label;
    switch (profileNav.from) {
      case 'members':
        return pathMap.members;
      case 'tree':
        return pathMap['hybrid-pedigree'];
      case 'add-self':
        return pathMap.overview;
      default:
        return profileParent?.label;
    }
  };

  const pushProfileParentCrumb = () => {
    const label = profileParentLabel();
    if (profileParent && label) {
      breadcrumbItems.push({
        label,
        path: profileParent.path,
        isClickable: true
      });
    }
  };

  const pushMyFamiliesCrumb = (isClickable: boolean) => {
    breadcrumbItems.push({
      label: t('breadcrumb.myFamilies'),
      path: MY_FAMILIES_PATH,
      isClickable
    });
  };

  if (isInFamilyContext && domainStore.currentFamily) {
    // Family context: My Families / Family Name / Current Page
    pushMyFamiliesCrumb(true);

    const familyName = domainStore.currentFamily.displayName || domainStore.currentFamily.name;
    breadcrumbItems.push({
      label: familyName,
      path: `/families/${familyId}/overview`,
      isClickable: true
    });

    // Add current page (Overview, Tree View, etc.) or person profile
    if (currentPath === `/families/${familyId}/overview`) {
      breadcrumbItems.push({ label: pathMap.overview, path: currentPath, isClickable: false });
    } else if (isPersonProfilePage) {
      const personDisplayName = personName || tCommon('person');
      pushProfileParentCrumb();

      if (isEditMode) {
        breadcrumbItems.push({
          label: personDisplayName,
          path: currentPath,
          isClickable: true
        });
        breadcrumbItems.push({ label: t('breadcrumb.edit'), path: currentPath, isClickable: false });
      } else {
        breadcrumbItems.push({
          label: personDisplayName,
          path: currentPath,
          isClickable: false
        });
      }
    } else if (isPetProfilePage) {
      pushProfileParentCrumb();
      breadcrumbItems.push({
        label: petName || tCommon('pet'),
        path: currentPath,
        isClickable: false
      });
    } else if (familyId && /\/memories\/[0-9a-f-]{36}$/i.test(currentPath)) {
      breadcrumbItems.push({
        label: pathMap.memories,
        path: `/families/${familyId}/memories`,
        isClickable: true
      });
      breadcrumbItems.push({ label: t('breadcrumb.memory'), path: currentPath, isClickable: false });
    } else {
      let currentPage = meaningfulSegment
        ? pathMap[meaningfulSegment as keyof typeof pathMap] || pathMap.overview
        : pathMap.overview;
      if (currentPath.includes('/pets/add')) currentPage = t('breadcrumb.addPet');
      breadcrumbItems.push({ label: currentPage, path: currentPath, isClickable: false });
    }
  } else if (isMainLevelRoute) {
    const isFamiliesList = currentPath === '/families' || currentPath === '/';

    if (isFamiliesList) {
      pushMyFamiliesCrumb(false);
    } else {
      pushMyFamiliesCrumb(true);
    }

    const mainPageLabel = isFamiliesList
      ? null
      : currentPath.startsWith('/profile')
        ? pathMap.profile
        : currentPath.startsWith('/create-family')
          ? pathMap['create-family']
          : currentPath.startsWith('/help')
            ? pathMap.help
            : currentPath.startsWith('/changelog')
              ? pathMap.changelog
              : meaningfulSegment
                ? pathMap[meaningfulSegment as keyof typeof pathMap] || t('breadcrumb.home')
                : t('breadcrumb.home');

    if (mainPageLabel) {
      if (isHelpPage && helpTabLabel) {
        breadcrumbItems.push({ label: mainPageLabel, path: '/help', isClickable: true });
        breadcrumbItems.push({ label: helpTabLabel, path: currentPath + location.search, isClickable: false });
      } else {
        breadcrumbItems.push({ label: mainPageLabel, path: currentPath, isClickable: false });
      }
    }
  } else {
    // Fallback for other routes (e.g. accept-invitation)
    pushMyFamiliesCrumb(true);
    const fallbackLabel = meaningfulSegment
      ? pathMap[meaningfulSegment as keyof typeof pathMap] || t('breadcrumb.home')
      : t('breadcrumb.home');
    breadcrumbItems.push({ label: fallbackLabel, path: currentPath, isClickable: false });
  }

  const totalItems = breadcrumbItems.length;
  const hasThreeOrMore = totalItems >= 3;

  return (
    <nav className="breadcrumb" aria-label={t('breadcrumb.ariaLabel')}>
      <ol className="breadcrumb__list">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isSecondToLast = hasThreeOrMore && index === totalItems - 2;

          // For person name link in edit mode, remove ?edit=true from URL
          // When clicking person name in edit mode, go to view mode (same path without query params)
          const linkPath =
            item.isClickable && isPersonProfilePage && isEditMode && index === totalItems - 2
              ? location.pathname // Use pathname without search params to exit edit mode
              : item.path;

          return (
            <li key={`${item.path}-${index}`} className="breadcrumb__item">
              {item.isClickable ? (
                <Link
                  to={linkPath}
                  className={`breadcrumb__link ${isSecondToLast ? 'breadcrumb__link--second-to-last' : ''}`}
                >
                  {item.label}
                </Link>
              ) : (
                <span className="breadcrumb__current" aria-current="page">
                  {item.label}
                </span>
              )}
              {!isLast && <span className="breadcrumb__separator">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
});

export default Breadcrumb;
